import React, { createContext, useState, useContext, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { ConfigProvider, message, theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import { 
  ThemeSettings, 
  DEFAULT_THEME_SETTINGS, 
  getThemeSettings, 
  saveThemeSettings, 
  getStoredThemeSettings, 
  saveStoredThemeSettings,
  PRIMARY_COLOR,
  COLOR_SCHEMES
} from '../services/themeService';

interface ThemeState {
  token: {
    colorPrimary: string;
    fontSize: number;
  };
  algorithm: typeof antdTheme.defaultAlgorithm[];
}

interface ThemeContextType {
  theme: ThemeState;
  updateTheme: (settings: Partial<ThemeSettings>) => Promise<void>;
  themeSettings: ThemeSettings;
}

const DEFAULT_THEME_STATE: ThemeState = {
  token: {
    colorPrimary: PRIMARY_COLOR,
    fontSize: DEFAULT_THEME_SETTINGS.fontSize,
  },
  algorithm: [antdTheme.defaultAlgorithm],
};

const ThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME_STATE,
  updateTheme: async () => {},
  themeSettings: DEFAULT_THEME_SETTINGS,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const storedThemeSettings = getStoredThemeSettings() || DEFAULT_THEME_SETTINGS;
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>(storedThemeSettings);
  const initialThemeState: ThemeState = {
    token: {
      colorPrimary: storedThemeSettings.colorScheme || COLOR_SCHEMES.GREEN,
      fontSize: storedThemeSettings.fontSize,
    },
    algorithm: storedThemeSettings.isDarkMode ? [antdTheme.darkAlgorithm] : [antdTheme.defaultAlgorithm],
  };

  const [theme, setTheme] = useState<ThemeState>(initialThemeState);
  const [messageApi, contextHolder] = message.useMessage();
  
  useEffect(() => {
    const loadTheme = async () => {
      try {
        if (getStoredThemeSettings()) {
          return;
        }
        
        const apiSettings = await getThemeSettings();
        if (apiSettings) {
          saveStoredThemeSettings(apiSettings);
          setThemeSettings(apiSettings);
          applyThemeSettings(apiSettings);
        }
      } catch (error) {
        console.error('Failed to load theme settings:', error);
      }
    };
    
    loadTheme();
  }, []);
  
  const applyThemeSettings = (settings: ThemeSettings) => {
    const primaryColor = settings.colorScheme || COLOR_SCHEMES.GREEN;
    
    setTheme({
      token: {
        colorPrimary: primaryColor,
        fontSize: settings.fontSize,
      },
      algorithm: settings.isDarkMode ? [antdTheme.darkAlgorithm] : [antdTheme.defaultAlgorithm],
    });
    
    document.documentElement.setAttribute('data-theme', settings.isDarkMode ? 'dark' : 'light');
    
    document.documentElement.style.setProperty('--primary-color', primaryColor);
    document.documentElement.style.setProperty('--background-color', settings.isDarkMode ? '#141414' : '#f5f7fa');
    document.documentElement.style.setProperty('--content-background', settings.isDarkMode ? '#1f1f1f' : '#ffffff');
    document.documentElement.style.setProperty('--font-size', `${settings.fontSize}px`);
  };
  
  const updateTheme = useCallback(async (newSettings: Partial<ThemeSettings>) => {
    try {
      const updatedSettings = {
        ...themeSettings,
        ...newSettings,
      };
      
      applyThemeSettings(updatedSettings);
      setThemeSettings(updatedSettings);      
      saveStoredThemeSettings(updatedSettings);
      await saveThemeSettings(updatedSettings);      
    } catch (error) {
      messageApi.error('Failed to update theme');
    }
  }, [themeSettings, messageApi]);
  
  const contextValue = useMemo(() => ({
    theme,
    updateTheme,
    themeSettings,
  }), [theme, updateTheme, themeSettings]);
  
  const configTheme = useMemo<ThemeConfig>(() => ({
    token: theme.token,
    algorithm: theme.algorithm,
    components: {
      Layout: {
        bodyBg: theme.algorithm[0] === antdTheme.darkAlgorithm ? '#141414' : '#f5f7fa',
        siderBg: theme.algorithm[0] === antdTheme.darkAlgorithm ? '#141414' : '#ffffff',
      }
    }
  }), [theme]);
  
  return (
    <ThemeContext.Provider value={contextValue}>
      {contextHolder}
      <ConfigProvider theme={configTheme}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}; 